import { resolveGutBodyConnectionsSystems } from '#components/GutBodyConnectionsBody/systems';
import type {
  GutBodyConnectionsColorVariant,
  GutBodyConnectionsOrgan,
} from '@repo/types';
import { useMemo } from 'react';
import styles from './GutBodyConnectionsOrganList.module.css';
import OrganRow from './OrganRow';

type GutBodyConnectionsOrganListProps = {
  organs: GutBodyConnectionsOrgan[];
  /** `kit.age_at_sampling`, in days. */
  age: number;
  pregnant: boolean;
  sex: string;
  /** The body map's `colorVariant`, for the organ badges. */
  colorVariant?: GutBodyConnectionsColorVariant;
  /** Omit to render a static list, as the PDF report would. */
  onOrganSelect?: (organ: GutBodyConnectionsOrgan) => void;
};

/**
 * The body map as a list, for viewports where the map cannot be hovered or
 * usefully tapped. It resolves its organs through the map's own resolver, so it
 * can never name a system the body has no drawing for.
 */
export function GutBodyConnectionsOrganList({
  organs,
  age,
  pregnant,
  sex,
  colorVariant,
  onOrganSelect,
}: GutBodyConnectionsOrganListProps) {
  const { listedSystems } = useMemo(
    () => resolveGutBodyConnectionsSystems({ organs, age, pregnant, sex }),
    [organs, age, pregnant, sex],
  );

  return (
    <ul className={styles.list}>
      {listedSystems.map(({ organ }) => (
        <OrganRow
          key={organ.id}
          organ={organ}
          age={age}
          pregnant={pregnant}
          colorVariant={colorVariant}
          onSelect={onOrganSelect ? () => onOrganSelect(organ) : undefined}
        />
      ))}
    </ul>
  );
}

export default GutBodyConnectionsOrganList;
